import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

export type BookingStep = 'service' | 'time' | 'contacts';

const KEYS = ['book', 'step', 'service', 'date', 'time'] as const;

interface FlowState {
  bookingDepth?: number;
}

/**
 * The booking sheet lives in the URL (?book=1&step=…&service=…&date=…&time=…).
 * Every step is a history entry, so the phone's Back button walks back one
 * step and finally closes the sheet; closing with ✕ pops all sheet entries.
 */
export function useBookingFlow() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const depth = (location.state as FlowState | null)?.bookingDepth ?? 0;

  const serviceId = params.get('service') ?? undefined;
  const date = params.get('date') ?? undefined;
  const time = params.get('time') ?? undefined;
  const isOpen = params.get('book') === '1';
  const step: BookingStep = (params.get('step') as BookingStep | null) ?? (serviceId ? 'time' : 'service');

  const push = useCallback(
    (patch: Partial<Record<(typeof KEYS)[number], string | undefined>>, opts: { replace?: boolean } = {}) => {
      const next = new URLSearchParams(location.search);
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined) next.delete(k);
        else next.set(k, v);
      }
      navigate(
        { pathname: location.pathname, search: `?${next.toString()}` },
        { replace: opts.replace, state: { bookingDepth: opts.replace ? depth : depth + 1 } satisfies FlowState, preventScrollReset: true },
      );
    },
    [depth, location.pathname, location.search, navigate],
  );

  const open = useCallback(
    (preselectServiceId?: string) =>
      push({ book: '1', service: preselectServiceId, step: preselectServiceId ? 'time' : 'service', date: undefined, time: undefined }),
    [push],
  );

  /** Jump straight to contacts for a slot suggested elsewhere (assistant). */
  const openAt = useCallback(
    (serviceId: string, startsAt: string) => push({ book: '1', service: serviceId, time: startsAt, step: 'contacts', date: undefined }),
    [push],
  );

  const close = useCallback(() => {
    if (depth > 0) {
      navigate(-depth);
    } else {
      const next = new URLSearchParams(location.search);
      for (const k of KEYS) next.delete(k);
      navigate({ pathname: location.pathname, search: next.toString() ? `?${next}` : '' }, { replace: true, preventScrollReset: true });
    }
  }, [depth, location.pathname, location.search, navigate]);

  return {
    isOpen,
    step,
    serviceId,
    date,
    time,
    open,
    openAt,
    close,
    back: () => navigate(-1),
    chooseService: (id: string) => push({ service: id, step: 'time', date: undefined, time: undefined }),
    chooseDate: (d: string) => push({ date: d, time: undefined }, { replace: true }),
    chooseTime: (iso: string) => push({ time: iso, step: 'contacts' }),
    toTime: () => push({ step: 'time', time: undefined }, { replace: true }),
    canGoBack: depth > 1,
  };
}
